import { useEffect, useRef } from "react";
import type { WidgetFeatures, WidgetTab } from "@/lib/widget-config";
import { isTabEnabled, pickInitialTab, routeToTab, type WidgetRoute } from "@/lib/widget-route";
import { defaultRouteFor } from "./helpers";

/**
 * The route for the operator's `defaultTab` + `features`. A disabled default falls back to the first
 * enabled tab. Landing on Messages resumes the latest thread: drill straight into the conversation view
 * (a loading skeleton) instead of flashing the list and jumping (see the shell's resume effect).
 */
export function routeForDefaultTab(defaultTab: WidgetTab, features: WidgetFeatures): WidgetRoute {
  const tab = pickInitialTab(defaultTab, features);
  if (tab === "messages") return { kind: "messages-thread", pendingLatest: true };
  return defaultRouteFor(tab);
}

/** The slice of the resolved config the initial route depends on. */
export interface InitialRouteInput {
  defaultTab: WidgetTab;
  features: WidgetFeatures;
  /** `useWidgetConfig().isLoading`: while true `defaultTab` is the bundled demo's, not the BE's. */
  loading: boolean;
}

/**
 * Keeps the shell's route on the correct landing tab. The shell owns `useState(initialRoute)`.
 *
 * The first render only has the bundled demo config (`defaultTab: "home"`), so the BE's `defaultTab`
 * arrives later. Policy: the first time the config resolves, if the visitor (or a host command, peek
 * action, deep link, restored thread) has NOT moved the route yet, snap ONCE to the resolved default.
 * Any route change before that wins, and nothing is ever snapped after that single decision.
 * Detection is by identity: a navigation always sets a new route object, so "untouched" means the
 * state is still the exact object created on mount.
 *
 * Also leaves a tab that gets disabled later (preview patches, config updates) for the default/first
 * enabled one.
 */
export function useInitialRoute(
  route: WidgetRoute,
  setRoute: (route: WidgetRoute) => void,
  { defaultTab, features, loading }: InitialRouteInput,
) {
  const mountRouteRef = useRef(route);
  const settledRef = useRef(false);

  // Snap to a valid tab whenever the feature set or default changes. Reads `route`, is not
  // triggered by it (a dependency would re-snap on every navigation).
  // biome-ignore lint/correctness/useExhaustiveDependencies: route is read inside the effect, not a trigger
  useEffect(() => {
    if (isTabEnabled(routeToTab(route), features)) return;
    setRoute(defaultRouteFor(pickInitialTab(defaultTab, features)));
  }, [features, defaultTab]);

  // Declared AFTER the disabled-tab snap so that, when the config resolves with a tab set that
  // disables the current tab, the resolved-default decision is the last write.
  // biome-ignore lint/correctness/useExhaustiveDependencies: decide once when the config first resolves; later route/config changes must not re-snap
  useEffect(() => {
    if (loading || settledRef.current) return;
    settledRef.current = true;
    if (route === mountRouteRef.current) setRoute(routeForDefaultTab(defaultTab, features));
  }, [loading]);
}
